import { supabase, isSupabaseConfigured } from './supabaseClient';
import { CaseFile, CalendarEvent, Note, LegalPayment, LegalDocument, AIPrompt } from '../types';

const getLocal = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(`lex_${key}`) || '[]');
  } catch (e) {
    console.error(`Eroare la parsarea datelor din localStorage pentru cheia: ${key}`, e);
    return [];
  }
};
const setLocal = (key: string, data: any) => localStorage.setItem(`lex_${key}`, JSON.stringify(data));

/**
 * databaseService.ts
 * Gestionarea persistenței datelor în mod hibrid (Supabase + LocalStorage)
 */
export const db = {
  // Gestionare AI Prompts
  async getPrompts(): Promise<AIPrompt[]> {
    if (!isSupabaseConfigured) return [];
    try {
      const { data, error } = await supabase.from('ai_prompts').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching AI prompts:', error);
      return [];
    }
  },
  async getPromptByName(name: string): Promise<AIPrompt | null> {
    if (!isSupabaseConfigured) return null;
    try {
      const { data, error } = await supabase.from('ai_prompts').select('*').eq('name', name).limit(1).single();
      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    } catch (error) {
      console.error('Error fetching AI prompt by name:', error);
      return null;
    }
  },
  async addPrompt(prompt: Omit<AIPrompt, 'id' | 'created_at' | 'updated_at'>): Promise<AIPrompt | null> {
    if (!isSupabaseConfigured) return null;
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Nu esti autentificat.');

      const { data, error } = await supabase.from('ai_prompts').insert([{
        user_id: userData.user.id,
        name: prompt.name,
        description: prompt.description,
        system_instruction: prompt.system_instruction,
        is_default: prompt.is_default
      }]).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding AI prompt:', error);
      throw error;
    }
  },
  async updatePrompt(id: string, updates: Partial<AIPrompt>): Promise<boolean> {
    if (!isSupabaseConfigured) return false;
    try {
      const { error } = await supabase.from('ai_prompts').update({
        name: updates.name,
        description: updates.description,
        system_instruction: updates.system_instruction,
        is_default: updates.is_default,
        updated_at: new Date().toISOString()
      }).eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating AI prompt:', error);
      throw error;
    }
  },
  async deletePrompt(id: string): Promise<boolean> {
    if (!isSupabaseConfigured) return false;
    try {
      const { error } = await supabase.from('ai_prompts').delete().eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting AI prompt:', error);
      throw error;
    }
  },

  // Obține lista dosarelor
  async getCases(): Promise<CaseFile[]> {
    if (!isSupabaseConfigured) return getLocal('cases');
    try {
      const { data, error } = await supabase.from('cases').select('*').order('opened_date', { ascending: false });
      if (error) throw error;
      return (data || []).map((c: any) => ({
        id: c.id,
        caseNumber: c.case_number,
        clientName: c.client_name,
        court: c.court,
        status: c.status,
        phase: c.phase || 'Fond',
        lastUpdate: c.last_update || new Date().toISOString(),
        nextHearingDate: c.next_hearing_date,
        openedDate: c.opened_date || new Date().toISOString(),
        difficulty: c.difficulty || 'medie',
        estimatedHours: c.estimated_hours || 0,
        actualHours: c.actual_hours || 0,
        resolution: c.resolution,
        activeDeadlines: c.active_deadlines
      }));
    } catch (err) {
      console.error("Eroare la preluarea dosarelor din Supabase:", err);
      return getLocal('cases');
    }
  },

  // Adaugă un dosar nou
  async addCase(c: Partial<CaseFile>): Promise<CaseFile | null> {
    if (!isSupabaseConfigured) {
      const cases = getLocal('cases');
      const newCase = {
        ...c,
        id: Math.random().toString(36).substring(2, 11),
        openedDate: new Date().toISOString(),
        lastUpdate: new Date().toISOString()
      };
      setLocal('cases', [newCase, ...cases]);
      return newCase as CaseFile;
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!userData.user) throw new Error('Not authenticated');

      const { data, error } = await supabase.from('cases').insert([{
        user_id: userData.user.id,
        case_number: c.caseNumber,
        client_name: c.clientName,
        court: c.court,
        status: c.status || 'În lucru',
        phase: c.phase || 'Fond',
        opened_date: new Date().toISOString()
      }]).select().single();
      
      if (error) throw error;

      if (data) {
        return {
          id: data.id,
          caseNumber: data.case_number,
          clientName: data.client_name,
          court: data.court,
          status: data.status,
          phase: data.phase || 'Fond',
          lastUpdate: data.last_update || new Date().toISOString(),
          nextHearingDate: data.next_hearing_date,
          openedDate: data.opened_date,
          difficulty: 'medie',
          estimatedHours: 0,
          actualHours: 0
        };
      }
      return null;
    } catch (err) {
      console.error("Eroare la adăugarea dosarului în Supabase:", err);
      return null;
    }
  },

  // Actualizează un dosar existent
  async updateCase(id: string, updates: Partial<CaseFile>): Promise<CaseFile | null> {
    if (!isSupabaseConfigured) {
      const cases = getLocal('cases');
      const updatedCases = cases.map((c: CaseFile) => c.id === id ? { ...c, ...updates, lastUpdate: new Date().toISOString() } : c);
      setLocal('cases', updatedCases);
      return updatedCases.find((c: CaseFile) => c.id === id) || null;
    }

    try {
      const { data, error } = await supabase.from('cases').update({
        case_number: updates.caseNumber,
        client_name: updates.clientName,
        court: updates.court,
        status: updates.status,
        phase: updates.phase,
        next_hearing_date: updates.nextHearingDate,
        resolution: updates.resolution
      }).eq('id', id).select().single();
      
      if (error) throw error;

      if (data) {
        return {
          id: data.id,
          caseNumber: data.case_number,
          clientName: data.client_name,
          court: data.court,
          status: data.status,
          phase: data.phase || 'Fond',
          lastUpdate: data.last_update,
          nextHearingDate: data.next_hearing_date,
          openedDate: data.opened_date,
          difficulty: 'medie',
          estimatedHours: 0,
          actualHours: 0,
          resolution: data.resolution
        };
      }
      return null;
    } catch (err) {
      console.error("Eroare la actualizarea dosarului în Supabase:", err);
      return null;
    }
  },

  // Șterge un dosar
  async deleteCase(id: string): Promise<boolean> {
    if (!isSupabaseConfigured) {
      const cases = getLocal('cases').filter((c: CaseFile) => c.id !== id);
      setLocal('cases', cases);
      return true;
    }

    try {
      const { error } = await supabase.from('cases').delete().eq('id', id);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error("Eroare la ștergerea dosarului din Supabase:", err);
      return false;
    }
  },

  // Gestionare Evenimente Calendar
  async getEvents(): Promise<CalendarEvent[]> { return getLocal('events'); },
  async addEvent(e: Omit<CalendarEvent, 'id'>): Promise<CalendarEvent> { 
    const evs = getLocal('events');
    const newEv = { ...e, id: Math.random().toString() };
    setLocal('events', [...evs, newEv]);
    return newEv as CalendarEvent;
  },
  async updateEvent(id: string, updates: Partial<CalendarEvent>) {
    const evs = getLocal('events');
    const updatedEvs = evs.map((e: CalendarEvent) => e.id === id ? { ...e, ...updates } : e);
    setLocal('events', updatedEvs);
  },
  async deleteEvent(id: string) { setLocal('events', getLocal('events').filter((x: CalendarEvent)=>x.id!==id)); },

  // Gestionare Notițe
  async getNotes(): Promise<Note[]> { return getLocal('notes'); },
  async addNote(n: Omit<Note, 'id' | 'createdAt'>): Promise<Note> {
    const ns = getLocal('notes');
    const newN = { ...n, id: Math.random().toString(), createdAt: new Date().toISOString() };
    setLocal('notes', [newN, ...ns]);
    return newN as Note;
  },
  async updateNote(id: string, updates: Partial<Note>): Promise<Note> {
    const ns = getLocal('notes');
    const index = ns.findIndex((x: Note) => x.id === id);
    if (index > -1) {
      ns[index] = { ...ns[index], ...updates };
      setLocal('notes', ns);
      return ns[index];
    }
    throw new Error("Note not found");
  },
  async deleteNote(id: string) { setLocal('notes', getLocal('notes').filter((x: Note)=>x.id!==id)); },

  // Gestionare Plăți
  async getPayments(): Promise<LegalPayment[]> { return getLocal('payments'); },
  async addPayment(p: Omit<LegalPayment, 'id'>): Promise<LegalPayment> {
    const ps = getLocal('payments');
    const newP = { ...p, id: Math.random().toString() };
    setLocal('payments', [newP, ...ps]);
    return newP as LegalPayment;
  },

  // Actualizează o plată
  async updatePayment(id: string, updates: Partial<LegalPayment>): Promise<LegalPayment | undefined> {
    const ps = getLocal('payments');
    const updatedPs = ps.map((p: LegalPayment) => p.id === id ? { ...p, ...updates } : p);
    setLocal('payments', updatedPs);
    return updatedPs.find((p: LegalPayment) => p.id === id);
  },
  async deletePayment(id: string) { setLocal('payments', getLocal('payments').filter((x: LegalPayment)=>x.id!==id)); },

  // Gestionare Documente
  async getDocuments(): Promise<LegalDocument[]> { return getLocal('documents'); },
  async addDocumentRecord(d: Omit<LegalDocument, 'id' | 'uploadDate' | 'filePath'>, path: string): Promise<LegalDocument> {
    const docs = getLocal('documents');
    const newD = { ...d, id: Math.random().toString(), filePath: path, uploadDate: new Date().toISOString() };
    setLocal('documents', [newD, ...docs]);
    return newD as LegalDocument;
  },

  // Șterge un document
  async deleteDocument(id: string, filePath?: string): Promise<boolean> {
    const docs = getLocal('documents').filter((d: LegalDocument) => d.id !== id);
    setLocal('documents', docs);
    return true;
  },

  // Încărcare fișier (Hibrid: Supabase Storage sau Local Fallback)
  async uploadFile(file: File): Promise<string> {
    // 1. Încercăm Supabase dacă este configurat
    if (isSupabaseConfigured) {
      try {
        const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const { data, error } = await supabase.storage
          .from('documents')
          .upload(fileName, file);

        if (error) throw error;
        if (data) return data.path;
      } catch (err) {
        console.warn("Eroare upload Supabase (posibil bucket lipsă/permisiuni). Trecem pe stocare locală.", err);
      }
    }

    // 2. Fallback: Stocare Locală (localStorage / IndexedDB simulat)
    const path = `local_${Date.now()}_${file.name}`;
    const reader = new FileReader();
    
    return new Promise<string>((resolve) => {
      reader.onload = (e) => {
        try {
          const fileData = getLocal('fileData') || {};
          fileData[path] = e.target?.result;
          // Clean-up rudimentar
          try {
             setLocal('fileData', fileData);
          } catch(e) {
             console.warn("Storage local plin. Șterg fișiere vechi...");
             const keys = Object.keys(fileData);
             if (keys.length > 5) {
                const newFileData: any = {};
                keys.slice(keys.length - 5).forEach(k => newFileData[k] = fileData[k]);
                newFileData[path] = e.target?.result;
                setLocal('fileData', newFileData);
             }
          }
        } catch(e) {}
        resolve(path);
      };
      reader.readAsDataURL(file);
    });
  },

  // Obține URL-ul fișierului
  async getFileUrl(path?: string): Promise<string | null> {
    if (!path) return null;

    // 1. Dacă e fișier local (începe cu local_)
    if (path.startsWith('local_')) {
      const fileData = getLocal('fileData') || {};
      const dataUrl = fileData[path];
      if (dataUrl) {
        try {
          // Conversie mult mai sigură a base64 în Blob URL direct prin fetch pentru a evita limitările atob
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch(e) {
          console.error("Eroare reconstrucție fișier local", e);
          return null;
        }
      }
      return null;
    }

    // 2. Dacă e fișier Supabase
    if (isSupabaseConfigured) {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(path, 60);
      
      if (error) {
        console.error("Eroare generare link securizat:", error);
        return null;
      }
      return data.signedUrl;
    }

    return path; 
  },

  // Stocheaza un chunk de document si embedding-ul sau
  async saveDocumentChunk(documentId: string, caseId: string | undefined, content: string, embedding: number[], metadata: any = {}) {
    if (!isSupabaseConfigured) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase.from('document_chunks').insert([{
        user_id: user.id,
        document_id: documentId,
        case_id: caseId || null,
        content,
        embedding,
        metadata
      }]);
      if (error) throw error;
    } catch (err) {
      console.error("Error saving document chunk:", err);
    }
  },

  // Cauta cele mai relevante chunk-uri pe baza unei interogari semantice
  async searchDocumentChunks(queryEmbedding: number[], matchCount: number = 3, matchThreshold: number = 0.5, caseId?: string): Promise<any[]> {
    if (!isSupabaseConfigured) return [];
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase.rpc('match_document_chunks', {
        query_embedding: queryEmbedding,
        match_threshold: matchThreshold,
        match_count: matchCount,
        p_user_id: user.id,
        p_case_id: caseId || null
      });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching document chunks:", err);
      return [];
    }
  },

  // Actualizare rezumat AI și text extras pentru document
  async updateDocumentSummary(id: string, summary: string, extractedText?: string, entities?: LegalDocument['entities'], analysis?: LegalDocument['analysis'], deadlines?: LegalDocument['deadlines']): Promise<LegalDocument | undefined> {
    const docs = getLocal('documents');
    const updatedDocs = docs.map((d: LegalDocument) =>
      d.id === id ? { ...d, summary, ...(extractedText ? { extractedText } : {}), ...(entities ? { entities } : {}), ...(analysis ? { analysis } : {}), ...(deadlines ? { deadlines } : {}) } : d
    );
    setLocal('documents', updatedDocs);
    return updatedDocs.find((d: LegalDocument) => d.id === id);
  }
};
